import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { requestSelectors } from 'src/store/requests/selectors';
import {
  ConfiguratorDisplayedTypeInner,
  ListOfKeysFilterAllData,
  PlacementDisplayedType,
} from 'src/typings/filter';
import { getComplexId, getCrmData } from 'src/store/app/selectors';

import {
  useFilterConfigurator,
  useTranslations,
  useBuildType,
} from 'src/hooks';
import { appIcon } from 'src/assets';
import { ApartmentConnectDeal } from 'src/components/ApartmentConnectDeal';
import { ButtonApp } from 'src/components/Button';
import { locals } from 'src/constants/locals';
import { Actions } from 'src/store/app/actions';
import { pathList } from 'src/route-list';
import { ApartmentImage } from 'src/components/LayoutApartmentItem/components/ApartmentImage';
import { getSelectedView } from 'src/store/type-displayed-spinner/selectors';
import { TypesOfSpinner } from 'src/typings/types-of-spinner';
import { useWritePositionScroll } from './useWritePositionScroll';
import { useStyles } from './styles';
import { ILayoutApartmentItem } from './types';
import {
  EyeIcon,
  FavIcon,
  InfoAdditional,
  InfoHeader,
  InfoIcon,
  InfoKey,
  InfoPlacing,
} from './components';

export const LayoutApartmentItem: React.FC<ILayoutApartmentItem> = React.memo(({
  isShowEye = false,
  withCloseButton = false,
  withBitrixDeal = false,
  isDisabledLink = false,
  isLayoutTab = false,
  isShowFavIcon = true,
  withMoreButton = false,
  placementDisplayedType = PlacementDisplayedType.filter,
  withSizeLimits = true,
  property,
  parentScroll,
  wrapperStyle,
  wrapperImageStyle,
  onClosePopover,
  imageChildren,
}) => {
  const dispatch = useDispatch();
  const mainColor = useSelector(requestSelectors.app.getMainColors);
  const getSelectedViewId = useSelector(getSelectedView);
  const isView2d = getSelectedViewId === TypesOfSpinner.view2d;
  const { isCrossTable } = useBuildType();
  const styles = useStyles({ mainColor, isView2d, isCrossTable });
  const { getTranslations } = useTranslations();
  const { handleWritePositionScroll } = useWritePositionScroll();
  const complexId = useSelector(getComplexId);
  const crmData = useSelector(getCrmData);
  const { filterConfigurator, paddingTileSize } = useFilterConfigurator(placementDisplayedType, property[ListOfKeysFilterAllData.id]);
  const mainImage = property.main_filter_img && property.main_filter_img.replace('desktop', 'mobile');
  const mainImageFromArray = property[ListOfKeysFilterAllData.placementGallery]?.length !== 0
    && property[ListOfKeysFilterAllData.placementGallery]?.[0].replace('desktop', 'mobile');
  const status = property?.[isCrossTable ? ListOfKeysFilterAllData.placementStatusCrossTableName : ListOfKeysFilterAllData.placementStatusModuleName];
  const complexPrefix = isCrossTable ? `/${complexId}` : '';

  const handleLinkClick = useCallback(() => {
    handleWritePositionScroll(parentScroll);
    dispatch(Actions.setIsOpenAppartSidebar(false));
  }, [dispatch, handleWritePositionScroll, parentScroll]);
  const getPlacementLink = useCallback((): string => {
    let link = pathList.own;
    if (placementDisplayedType === PlacementDisplayedType.placementGroupedTileConfig) {
      if (property.id.length > 1) {
        link = pathList.ownV2;
      }
    }
    return link;
  }, [placementDisplayedType]);
  return (
    <>
      {withCloseButton && (
        <Box className={styles.closeButtonPopover} onClick={onClosePopover}>
          <CloseIcon />
        </Box>
      )}
      <Box
        minWidth={withCloseButton ? 210 : 'none'}
        pt={withCloseButton ? 3 : 0}
        className={`${styles.wrapperProperty} ${
          isDisabledLink ? styles.disableLink : ''} ${wrapperStyle}`}
      >
        {isShowFavIcon && <FavIcon key={property.id} itemId={property.id} />}
        {
          !isLayoutTab
          && !isDisabledLink
          && isShowEye
          && (
            <EyeIcon
              itemId={property.id}
              property={property}
            />
          )
        }
        <>
          { imageChildren && (
            imageChildren
          ) }
        </>
        <Link
          to={`${!isDisabledLink ? `${complexPrefix}${getPlacementLink()}/${property.id}` : ''}`}
          onClick={handleLinkClick}
        >
          <ApartmentImage
            wrapperClassName={`${styles.wrapperImageProperty}
             ${withSizeLimits && styles.imageSizeLimits}
             ${!withSizeLimits && styles.imageAspectRatioSizeLimits}
              ${wrapperImageStyle || ''}`}
            wrapperStyle={paddingTileSize ? (isCrossTable ? {
              // CRM widget redesign: shrink the plan into the popover card.
              objectFit: 'contain',
              maxWidth: '186px',
              maxHeight: '140px',
              margin: '0 auto',
            } : {
              // Module keeps the backend-driven tile padding as before.
              paddingLeft: `${paddingTileSize}px`,
              paddingTop: `${paddingTileSize}px`,
              paddingRight: `${paddingTileSize}px`,
              objectFit: 'contain',
            }) : undefined}
            imageClassName={`${styles.imageStyle}`}
            imageStyle={paddingTileSize ? { objectFit: 'contain', borderRadius: '0px' } : undefined}
            imageSrc={mainImage || mainImageFromArray || appIcon.noImg.default}
            imageChildren={imageChildren}
          />
          {filterConfigurator && (
          <div className={styles.wrapperApartmentInfo}>
            <InfoHeader
              itemData={property}
              filterConfigurator={filterConfigurator}
              displayedType={ConfiguratorDisplayedTypeInner.headerInfo}
            />
            <InfoPlacing
              itemData={property}
              filterConfigurator={filterConfigurator}
              displayedType={ConfiguratorDisplayedTypeInner.placingInfo}
            />
            <InfoKey
              itemData={property}
              filterConfigurator={filterConfigurator}
              displayedType={ConfiguratorDisplayedTypeInner.keyInfo}
            />
            <InfoIcon
              itemData={property}
              filterConfigurator={filterConfigurator}
              displayedType={ConfiguratorDisplayedTypeInner.iconsAndShortInfo}
            />
            <InfoAdditional
              itemData={property}
              filterConfigurator={filterConfigurator}
              displayedType={ConfiguratorDisplayedTypeInner.additionalInfo}
            />
            {/* {(isCrossTable && !isLayoutTab && !isView2d) && ( */}
            {/* <ApartmentStatus property={property} /> */}
            {/* )} */}
            {withMoreButton && (
              <Box mt="10px">
                <ButtonApp
                  disabled={isDisabledLink}
                  fullWidth
                  text={
                    `${isDisabledLink ? status : getTranslations(locals.appTranslations.appMore)}`
                  }
                  variant="contained"
                />
              </Box>
            )}
          </div>
          )}
        </Link>
        {withBitrixDeal && crmData && (
          <Box marginTop="6px" display="flex" gap="16px" flexDirection="column">
            {/* unitId: hover popovers (Floors/3D) are about THIS unit — the
                store's selected apartment there is stale. */}
            <ApartmentConnectDeal placeInfo={property} isSmall unitId={String(property.id)} />
          </Box>
        )}
      </Box>
    </>
  );
});
